import {
  SoapSectionKeys,
  Soaps,
  SoapContent,
  Selection,
  HeaderSelection,
} from '@/shared/types/yakurekiSoap'
import { soapLabels, USER_IDS } from '@/shared/constants/const'

/**
 * フォーマットオプション
 */
interface FormatSoapTextOptions {
  /**
   * 特別フォーマットを使用するか
   * - true: セクション間を '--------------------------------------' で区切り
   * - false: 通常フォーマット（セクションラベル + 改行 + 内容）
   */
  useSpecialFormat?: boolean
}

/**
 * SOAP セクションキー一覧
 */
const SOAP_SECTION_KEYS: SoapSectionKeys[] = [
  'subjectives',
  'objectives',
  'assessments',
  'educational_plans',
  'care_plans',
  'observational_plans',
]

/**
 * 選択されたSOAP項目をテキスト形式にフォーマット
 *
 * @description
 * ADR-055 対応: 新シグネチャ版
 *
 * 重要な変更点:
 * - content はマスターデータ（soaps）から取得
 * - selection は Set<number>（インデックスのみ）
 * - headerSelection はヘッダーの選択状態
 *
 * @param selection - 選択状態（セクションごとの選択インデックス Set）
 * @param headerSelection - ヘッダー選択状態
 * @param soaps - SOAPマスターデータ
 * @param options - フォーマットオプション
 * @returns フォーマットされたテキスト
 *
 * @example
 * ```ts
 * const text = formatSoapText(
 *   { subjectives: new Set([0, 1]), ... },
 *   new Set(['subjectives']),
 *   soaps,
 *   { useSpecialFormat: false }
 * )
 * // 【S】
 * // 患者は頭痛を訴えている
 * // 発熱がある
 * ```
 *
 * @see ADR-055: SelectionContext の Content 同期問題の根本的解決
 */
export const formatSoapText = (
  selection: Selection | null,
  headerSelection: HeaderSelection | null,
  soaps: Soaps | SoapContent | null | undefined,
  options: FormatSoapTextOptions = {}
): string => {
  if (!selection || !soaps) return ''

  const { useSpecialFormat = false } = options
  const headers = headerSelection || new Set<SoapSectionKeys>()

  const formattedText = SOAP_SECTION_KEYS.map((key) => {
    const indices = selection[key]
    const sectionItems = soaps[key] || []
    const sectionLabel = soapLabels[key]

    // ヘッダーが選択されているかチェック
    const hasHeader = headers.has(key)

    // 選択されたインデックスがない場合
    if (!indices || indices.size === 0) {
      // ヘッダーのみ選択されている場合（空のセクション）
      if (hasHeader && sectionItems.length === 0) {
        return sectionLabel
      }
      return ''
    }

    // マスターデータから content を取得（ソート済み）
    const sortedIndices = Array.from(indices).sort((a, b) => a - b)
    const contentLines = sortedIndices
      .map((index) => sectionItems[index]?.content)
      .filter(Boolean)

    if (contentLines.length === 0) {
      // 選択インデックスはあるが、対応するアイテムがない場合
      if (hasHeader) {
        return sectionLabel
      }
      return ''
    }

    const content = contentLines.join('\n')

    if (useSpecialFormat) {
      // 特別フォーマット: ヘッダーなし、内容のみ
      return content
    } else {
      // 通常フォーマット: セクションラベル + 改行 + 内容
      return `${sectionLabel}\n${content}`
    }
  })
    .filter(Boolean)
    .join(
      useSpecialFormat ? '\n--------------------------------------\n' : '\n'
    )

  return formattedText
}

/**
 * 現在のユーザーが特別フォーマットを使用するかを判定
 *
 * @returns 特別フォーマットを使用する場合 true
 */
export const shouldUseSpecialFormat = (): boolean => {
  const userId = localStorage.getItem('userId')
  return userId === `"${USER_IDS.SPECIAL_FORMAT}"`
}
